import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import AuthService from '../services/authService';
import { API_BASE } from '../config/api';
import { CURRENT_TERMS_VERSION, TERMS_LAST_UPDATED } from '../config/termsVersion';
import Modal from './shared/Modal';

function TermsAcceptanceModal({ isOpen, onAccepted }) {
  const { t } = useTranslation('modals');
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/auth/accept-terms`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ version: CURRENT_TERMS_VERSION })
      });
      const result = await response.json();
      if (result.success) {
        onAccepted();
      } else {
        setError(t('termsAcceptance.errors.acceptFailedFallback'));
      }
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError(t('termsAcceptance.errors.unexpectedError'));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} size="lg" closeOnBackdrop={false} closeOnEscape={false} backdrop="dark" ariaLabel={t('termsAcceptance.header.modalAriaLabel')}>
      <div className="flex flex-col max-h-[85dvh]">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-content-primary">{t('termsAcceptance.header.title')}</h2>
          <p className="text-sm text-content-secondary mt-1">{t('termsAcceptance.header.subtitle')}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 text-sm text-content-secondary space-y-4">
          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section1.heading')}</h3>
          <p>
            {t('termsAcceptance.section1.body')}
          </p>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section2.heading')}</h3>
          <p>
            {t('termsAcceptance.section2.body')}
          </p>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section3.heading')}</h3>
          <p>
            {t('termsAcceptance.section3.intro')}
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t('termsAcceptance.section3.items.csam')}</li>
            <li>{t('termsAcceptance.section3.items.threats')}</li>
            <li>{t('termsAcceptance.section3.items.harassment')}</li>
            <li>{t('termsAcceptance.section3.items.sexualContent')}</li>
            <li>{t('termsAcceptance.section3.items.spam')}</li>
            <li>{t('termsAcceptance.section3.items.ipInfringement')}</li>
            <li>{t('termsAcceptance.section3.items.piiWithoutConsent')}</li>
          </ul>
          <p>
            {t('termsAcceptance.section3.reportingNote')}
          </p>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section4.heading')}</h3>
          <p>
            {t('termsAcceptance.section4.body')}
          </p>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section5.heading')}</h3>
          <p>
            {t('termsAcceptance.section5.reportingBody')}
          </p>
          <p>
            {t('termsAcceptance.section5.enforcementIntro')}
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><Trans t={t} i18nKey="termsAcceptance.section5.enforcementItems.warning"><strong>Warning</strong> &mdash; The content creator is notified that their content may breach these guidelines</Trans></li>
            <li><Trans t={t} i18nKey="termsAcceptance.section5.enforcementItems.contentRemoved"><strong>Content removal</strong> &mdash; The content is taken down from public surfaces and the creator is notified, with instructions for appealing</Trans></li>
          </ul>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section6.heading')}</h3>
          <p>
            <Trans t={t} i18nKey="termsAcceptance.section6.body">
              If your content is removed, you may appeal the decision by contacting us
              at <span className="text-primary">support@liveyourfun.com</span>.
              We will review your appeal and respond within 7 business days.
            </Trans>
          </p>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section7.heading')}</h3>
          <p>
            {t('termsAcceptance.section7.body')}
          </p>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section8.heading')}</h3>
          <p>
            {t('termsAcceptance.section8.intro')}
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t('termsAcceptance.section8.items.locationData')}</li>
            <li>{t('termsAcceptance.section8.items.photoStorage')}</li>
            <li>{t('termsAcceptance.section8.items.dataRequests')}</li>
          </ul>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section9.heading')}</h3>
          <p>
            {t('termsAcceptance.section9.body')}
          </p>
          <p>
            <Trans t={t} i18nKey="termsAcceptance.section9.contactLine">
              Law enforcement requests should be directed to <span className="text-primary">legal@liveyourfun.com</span>.
            </Trans>
          </p>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section10.heading')}</h3>
          <p>
            {t('termsAcceptance.section10.body')}
          </p>

          <h3 className="font-semibold text-content-primary">{t('termsAcceptance.section11.heading')}</h3>
          <p>
            {t('termsAcceptance.section11.body')}
          </p>

          <p className="text-xs text-content-muted mt-4">
            {t('termsAcceptance.footer.versionLine', { version: CURRENT_TERMS_VERSION, lastUpdated: TERMS_LAST_UPDATED })}
          </p>
        </div>

        <div className="p-6 border-t border-border">
          {error && (
            <p className="text-sm text-error mb-3 text-center">{error}</p>
          )}
          <button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full py-3 bg-primary text-content-inverse font-medium rounded-lg hover:bg-primary-dark transition-colors duration-fast disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
          >
            {accepting ? t('termsAcceptance.footer.acceptingLabel') : t('termsAcceptance.footer.acceptButton')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default TermsAcceptanceModal;
